import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { Fragment, useCallback, useEffect, useState } from "react";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { GlobalSearchBarProps, Option } from "./types";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { responsiveAppBarStyles } from "./styles";
import { handleGlobalSearch } from "../../services/globalSearchService";
import SearchIcon from "@mui/icons-material/Search";
import Mixpanel from "../../utils/mixpanel";
import { TEXT_COLOR } from "../../theme";

export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  userId,
  allowDisplaySearchBar,
  setAllowDisplaySearchBar,
}) => {
  const [open, setOpen] = useState(false);
  const [queryValue, setQueryValue] = useState<string | null>(null);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const { getValidAccessToken } = useGetValidAccessToken();
  const navigate = useNavigate();

  const handleQueryChange = async (e: string) => {
    setQueryValue(e);
    if (e.length === 0) {
      setQueryValue(null);
      setLoading(false);
      setOptions([]);
    } else {
      const accessToken = await getValidAccessToken();
      setLoading(true);
      setTimeout(async () => {
        if (queryValue !== "") {
          Mixpanel.track("global_search_bar_query");
          const response = await handleGlobalSearch(
            accessToken,
            userId,
            queryValue!
          );
          if (response) setLoading(false);
          const newTrackListArray = response.getTracksList().map((el) => {
            return {
              title: el.getTitle(),
              group: response.getTracksList().length > 1 ? "Tracks" : "Track",
              image: el.getImageUrl(),
              moreDetails: el,
              id: el.getId(),
            };
          });
          const newTrackFilesListArray = response
            .getTrackFilesList()
            .map((el) => {
              return {
                title: el.getFilename(),
                group:
                  response.getTrackFilesList().length > 1
                    ? "Track files"
                    : "Track file",
                image: "/src/ts/app/public/assets/images/logo512.png",
                moreDetails: el,
                id: el.getId(),
              };
            });
          const newUsersListArray = response.getUsersList().map((el) => {
            return {
              title: el.getUsername(),
              group: response.getUsersList().length > 1 ? "Users" : "User",
              image: el.getProfilePicture(),
              moreDetails: el,
              id: el.getUserId(),
            };
          });
          setOptions([
            ...newTrackListArray,
            ...newTrackFilesListArray,
            ...newUsersListArray,
          ]);
        } else {
          setLoading(false);
          setOptions([]);
        }
      }, 1000);
    }
  };

  const handleSearchBarExpansion = () => {
    setAllowDisplaySearchBar(true);
  };

  const handleNavigation = (option: any) => {
    if (option.group.includes("Track")) {
      const id = option.moreDetails.getId();
      navigate(`/track/${id}`);
    } else if (option.group.includes("User")) {
      const userName = option.moreDetails.getUsername();
      navigate(`/user-profile/${userName}`);
    }
  };

  const handleClick = useCallback((e: any) => {
    if (e.target.localName !== "input" && e.target.localName !== "svg") {
      setOptions([]);
    }
  }, []);

  useEffect(() => {
    document && document.addEventListener("click", handleClick);
    return () => {
      document && document.removeEventListener("click", handleClick);
    };
  }, [handleClick]);

  useEffect(() => {
    const keyDownHandler = (event: any) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setQueryValue(null);
        setAllowDisplaySearchBar(false);
      }
    };
    document && document.addEventListener("keydown", keyDownHandler);
    return () => {
      document && document.removeEventListener("keydown", keyDownHandler);
    };
  }, [setAllowDisplaySearchBar, setQueryValue]);

  return (
    <>
      <Box
        id="step-3"
        sx={{ marginRight: "10px" }}
        onClick={handleSearchBarExpansion}
      >
        <Autocomplete
          id="asynchronous-demo"
          sx={{
            ...responsiveAppBarStyles.autoComplete,
            ...{
              width: open ? "320px" : "30px",
              "& .MuiOutlinedInput-root": {
                border: "none",
                "& fieldset": {
                  border: !open ? "none" : `1px solid ${TEXT_COLOR}`,
                },
              },
            },
          }}
          open={open}
          noOptionsText={"No results found"}
          clearOnEscape={true}
          onOpen={() => {
            Mixpanel.track("global_search_bar_open");
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
            // clear();
            setAllowDisplaySearchBar(false);
          }}
          isOptionEqualToValue={(option, value) => option.title === value.title}
          getOptionLabel={(option) => option.title}
          groupBy={(option) => option.group}
          options={options}
          loading={loading}
          renderOption={(_props, option) => (
            <Box key={option.id}>
              <Box
                onClick={() => handleNavigation(option)}
                sx={responsiveAppBarStyles.searchContainer}
              >
                <img
                  src={
                    option.image.includes("http")
                      ? option.image
                      : `/assets/images/logo512.png`
                  }
                  alt={""}
                  style={responsiveAppBarStyles.searchImage}
                />
                <Box sx={responsiveAppBarStyles.titleContainer}>
                  {option.title}
                </Box>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <>
              {!allowDisplaySearchBar && (
                <SearchIcon
                  sx={{
                    position: "absolute",
                    margin: "15px 0 0 10px",
                    color: "white",
                  }}
                />
              )}
              <TextField
                {...params}
                value={queryValue || ""}
                placeholder={
                  allowDisplaySearchBar
                    ? "Search Tracks, Files, and Artists"
                    : ""
                }
                sx={{
                  color: "white",
                  position: "relative",
                  caretColor: !allowDisplaySearchBar ? "transparent" : "none",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    handleQueryChange("");
                    setAllowDisplaySearchBar(false);
                  }
                }}
                onChange={(e) => {
                  handleQueryChange(e.target.value);
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: allowDisplaySearchBar && (
                    <SearchIcon sx={{ color: "white" }} id="searchIcon" />
                  ),
                  endAdornment: open && (
                    <Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
              />
            </>
          )}
        />
      </Box>
    </>
  );
};
